import { Component, inject, OnInit, OnDestroy, signal, computed, input } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { PraticaDaFirmare, PraticaDetailResponse } from '../../../shared/models/pratica.model';
import { FirmaService } from '../../../shared/services/firma.service';
import { AuthService } from '../../../shared/services/auth.service';

interface FlattenedPage {
	documentID: number;
	localPage: number;
	globalPage: number;
}

import { HeaderComponent } from '../shared/components/header/header.component';
import { CommonModule } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModaleRifiutaComponent } from '../../../shared/components/modali/modale-rifiuta/modale-rifiuta.component';
import { ModaleSuccessoComponent } from '../../../shared/components/modali/modale-successo/modale-successo.component';
import { ModaleErroreGenericoComponent } from '../../../shared/components/modali/modale-errore-generico/modale-errore-generico.component';
import { ModaleConfermaAzioneComponent } from '../../../shared/components/modali/modale-conferma-azione/modale-conferma-azione.component';
import { ModaleOtpComponent } from '../../../shared/components/modali/modale-otp/modale-otp.component';

@Component({
	selector: 'app-dettaglio-pratica',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './dettaglio-pratica.component.html',
})
export class DettaglioPraticaComponent implements OnInit, OnDestroy {
	router = inject(Router);
	firmaService = inject(FirmaService);
	sanitizer = inject(DomSanitizer);
	authService = inject(AuthService);
	modalService = inject(NgbModal);

	idTransaction = input<string>();
	isPublic = input<boolean>(false);
	signatureCompleted = signal(false);

	companyName = computed(() => this.authService.currentUser?.titolo ?? '');

	pratica = signal<PraticaDaFirmare | null>(null);
	praticaDetail = signal<PraticaDetailResponse | null>(null);

	// Obsolete: currentDocIndex removed in favor of flattenedPages mapping
	currentPage = signal(1);
	totalPages = signal(1);
	pageUrls = signal<SafeUrl[]>([]);
	loadingPage = signal(false);
	loadingAction = signal(false);
	flattenedPages = signal<FlattenedPage[]>([]);

	operationID = signal<string | null>(null);
	gpsInfo = signal<string>('');

	pendingSignatureField = signal<any>(null);
	isFullscreen = signal(false);
	zoomLevel = signal(0.75);
	showDettaglioSheet = signal(false);
	isSheetClosing = signal(false);
	thumbnailCache = new Map<number, SafeUrl>();
	modalOpen = false;
	firmaMultipla = signal(true);

	showFirmaMultipla = computed(() => {
		const pd = this.praticaDetail();
		return pd?.tipoFirma === 'R' && !!this.authService.currentUser?.cliente?.multiSignOnview;
	});

	multiValue = computed(() => this.showFirmaMultipla() && this.firmaMultipla());

	getFieldsForPage(globalPage: number) {
		const pd = this.praticaDetail();
		const flat = this.flattenedPages();
		if (!pd?.signatureFields || !flat.length) return [];

		const pageInfo = flat[globalPage - 1];
		if (!pageInfo) return [];

		return pd.signatureFields.filter((f) => f.page === pageInfo.localPage && f.documentID === pageInfo.documentID);
	}

	getPagesForDocument(docIdx: number) {
		const pd = this.praticaDetail();
		const flat = this.flattenedPages();
		if (!pd?.documents?.[docIdx] || !flat.length) return [];

		const documentID = pd.documents[docIdx].documentID;
		return flat.filter((p) => p.documentID === documentID);
	}

	numDocs = computed(() => {
		const pd = this.praticaDetail();
		const p = this.pratica();
		return pd?.documents?.length ?? p?.documenti?.length ?? 0;
	});

	canPrev = computed(() => this.currentPage() > 1);
	canNext = computed(() => this.currentPage() < this.totalPages());

	pagesWithSignature = computed(() => {
		const pd = this.praticaDetail();
		if (!pd?.signatureFields) return new Set<string>();
		return new Set(pd.signatureFields.map((f) => `${f.documentID}-${f.page}`));
	});

	allRequiredSigned = computed(() => {
		const pd = this.praticaDetail();
		if (!pd?.signatureFields) return true;
		return pd.signatureFields.filter((f) => f.required).every((f) => f.firmato);
	});

	isAllSigned = computed(() => {
		const pd = this.praticaDetail();
		if (!pd?.signatureFields) return true;
		return pd.signatureFields.every((f) => f.firmato);
	});

	firstUnsignedField = computed(() => {
		const pd = this.praticaDetail();
		const flat = this.flattenedPages();
		if (!pd?.signatureFields || !flat.length) return null;

		const unsigned = pd.signatureFields.filter((f) => !f.firmato);
		if (unsigned.length === 0) return null;

		const docs = pd.documents;
		// Sort by doc index then local page number
		return unsigned.sort((a, b) => {
			const idxA = docs.findIndex((d) => d.documentID == a.documentID);
			const idxB = docs.findIndex((d) => d.documentID == b.documentID);
			if (idxA !== idxB) return idxA - idxB;
			return (a.page || 0) - (b.page || 0);
		})[0];
	});

	isAtFirstUnsignedPage = computed(() => {
		const field = this.firstUnsignedField();
		const flat = this.flattenedPages();
		if (!field || !flat.length) return false;

		const pageNum = this.currentPage();
		const pageInfo = flat[pageNum - 1];
		if (!pageInfo) return false;

		return field.documentID === pageInfo.documentID && field.page === pageInfo.localPage;
	});

	docName = computed(() => {
		const pd = this.praticaDetail();
		const flat = this.flattenedPages();
		const pageNum = this.currentPage();
		if (!pd?.documents?.length || !flat.length || !pageNum) return '';

		const pageInfo = flat[pageNum - 1];
		if (!pageInfo) return '';

		const doc = pd.documents.find((d) => d.documentID === pageInfo.documentID);
		return doc?.fileName || '';
	});

	ngOnInit() {
		const nav = this.router.getCurrentNavigation();
		const state = nav?.extras?.state || history.state;
		// Handle both direct state and Angular's 'usr' wrapper
		const navState = state && (state as any).usr ? (state as any).usr : state;
		const statePratica = navState?.pratica ?? null;

		if (statePratica) {
			this.pratica.set(statePratica);
		}

		if (navState?.authenticated) {
			this.isFirstLoadAuthenticated = true;
		}

		if (navState?.operationID) {
			this.operationID.set(navState.operationID);
		}

		this.getGpsInfo();

		const id = this.idTransaction();
		if (id) {
			this.loadDetail(id);
		}
	}

	ngOnDestroy() {
		this.firmaService.clearAuthCache();
	}

	private isFirstLoadAuthenticated = false;

	loadDetail(idTransaction: string, isRefresh: boolean = false) {
		if (!isRefresh) this.loadingPage.set(true);
		// Check both navigation state and service-level cache
		const alreadyAuth = this.isFirstLoadAuthenticated || !!this.firmaService.getAuthOperationID(idTransaction);

		// If we have a cached operationID, use it
		const cachedOpId = this.firmaService.getAuthOperationID(idTransaction);
		if (cachedOpId) {
			this.operationID.set(cachedOpId);
		}

		// Reset after first check
		this.isFirstLoadAuthenticated = false;

		this.firmaService.getPraticaDetail(idTransaction).subscribe({
			next: (statusRes: any) => {
				const token = statusRes.operationToken;
				const currentOpId = this.operationID();

				if (alreadyAuth && currentOpId) {
					this.praticaDetail.set(statusRes);
					this.setupViewer();
					this.loadingPage.set(false);
					return;
				}

				this.firmaService.startAuthOperation(idTransaction, token).subscribe({
					next: (authRes: any) => {
						const opId = authRes.operationID;
						this.operationID.set(opId);

						if (alreadyAuth) {
							this.praticaDetail.set(statusRes);
							this.setupViewer();
							this.loadingPage.set(false);
							return;
						}

						if (statusRes.authFirst) {
							this.loadingPage.set(false);
							const modalRef = this.modalService.open(ModaleOtpComponent, {
								centered: true,
								backdrop: 'static',
								keyboard: false,
								windowClass: 'modal-otp-custom modal-app-custom',
							});
							// Prepare inputs for SMS mode
							modalRef.componentInstance.mode = 'sms';
							modalRef.componentInstance.phone = statusRes.phone || '';
							modalRef.componentInstance.operationRef = opId;
							modalRef.componentInstance.onResend.subscribe(() => {
								this.firmaService.startAuthOperation(idTransaction, token).subscribe({
									next: (newAuthRes: any) => {
										modalRef.componentInstance.operationRef = newAuthRes.operationID;
										modalRef.componentInstance.resetTimer();
									},
									error: () => {
										this.showError('Errore', "Impossibile ricaricare il codice OTP.");
									}
								});
							});

							modalRef.result.then(
								(otpCode) => {
									if (otpCode) {
										this.firmaService.openTransaction(idTransaction, token, parseInt(otpCode, 10), opId).subscribe({
											next: () => {
												this.praticaDetail.set(statusRes);
												this.setupViewer();
											},
											error: () => this.showError('Errore', 'Codice OTP non valido.'),
										});
									} else {
										this.goBack();
									}
								},
								() => this.goBack(),
							);
						} else {
							this.firmaService.openTransaction(idTransaction, token, 0, opId).subscribe({
								next: () => {
									this.praticaDetail.set(statusRes);
									this.setupViewer();
									this.loadingPage.set(false);
								},
								error: () => {
									this.loadingPage.set(false);
									this.showError('Errore', "Si è verificato un errore durante l'apertura automatica della transazione.");
								},
							});
						}
					},
					error: () => {
						this.loadingPage.set(false);
						this.showError('Errore', "Si è verificato un errore durante l'avvio dell'operazione di autenticazione.");
					},
				});
			},
			error: () => {
				this.loadingPage.set(false);
				this.showError('Errore', 'Si è verificato un errore durante il recupero dello stato della pratica.');
			},
		});
	}

	showError(title: string, message: string) {
		const modalRef = this.modalService.open(ModaleErroreGenericoComponent, { centered: true, windowClass: 'modal-app-custom' });
		modalRef.componentInstance.title = title;
		modalRef.componentInstance.message = message;
	}

	showSuccess(title: string, message: string) {
		const modalRef = this.modalService.open(ModaleSuccessoComponent, { centered: true, windowClass: 'modal-app-custom' });
		modalRef.componentInstance.title = title;
		modalRef.componentInstance.message = message;
		return modalRef;
	}

	getGpsInfo() {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(pos) => {
					this.gpsInfo.set(`${pos.coords.latitude},${pos.coords.longitude}`);
				},
				(err) => {
					console.warn('Geolocation error:', err);
					this.gpsInfo.set('');
				},
			);
		}
	}

	setupViewer() {
		this.loadAllPages();
	}

	loadAllPages() {
		const idTrans = this.idTransaction();
		const pd = this.praticaDetail();
		const token = pd?.operationToken;

		if (!idTrans || !token || !pd?.documents?.length) return;

		const flat: FlattenedPage[] = [];
		let globalCount = 1;

		const requests: any[] = [];
		for (const doc of pd.documents) {
			const totalPages = doc.pages ?? 1;
			for (let i = 1; i <= totalPages; i++) {
				flat.push({
					documentID: doc.documentID,
					localPage: i,
					globalPage: globalCount++,
				});
				requests.push(this.firmaService.getPage(idTrans, doc.documentID, i, token));
			}
		}

		// Only reload if flattenedPages is actually different or empty
		const currentFlat = this.flattenedPages();
		if (
			currentFlat.length === flat.length &&
			currentFlat.every((p, i) => p.documentID === flat[i].documentID && p.localPage === flat[i].localPage)
		) {
			this.loadingPage.set(false);
			return;
		}

		this.loadingPage.set(true);
		this.pageUrls.set([]);

		this.totalPages.set(flat.length);
		this.flattenedPages.set(flat);

		import('rxjs').then(({ forkJoin }) => {
			forkJoin(requests).subscribe({
				next: (results: any[]) => {
					const urls = results.map((res: any) => {
						const url = `data:image/jpeg;base64,${res.pageBase64}`;
						return this.sanitizer.bypassSecurityTrustUrl(url);
					});
					this.pageUrls.set(urls);
					this.loadingPage.set(false);
				},
				error: () => {
					this.loadingPage.set(false);
					this.showError('Errore caricature', 'Si è verificato un errore durante il caricamento delle pagine del documento.');
				},
			});
		});
	}

	onScroll(event: Event) {
		const element = event.target as HTMLElement;
		const scrollTop = element.scrollTop;
		const scrollHeight = element.scrollHeight;
		const clientHeight = element.clientHeight;

		const totalPages = this.totalPages();
		if (totalPages <= 1) return;

		// Calculate using midpoint for better accuracy when scrolling back up
		const midPoint = scrollTop + clientHeight / 2;
		const pageHeight = scrollHeight / totalPages;
		const newPage = Math.min(Math.floor(midPoint / pageHeight) + 1, totalPages);

		if (newPage !== this.currentPage()) {
			this.currentPage.set(newPage);
		}
	}

	getUrlMiniaturaPagina(pageNum: number): SafeUrl | string {
		const urls = this.pageUrls();
		return urls[pageNum - 1] || '';
	}

	prevPage() {
		if (this.currentPage() > 1) {
			this.goToPageSelect(this.currentPage() - 1, false);
		}
	}

	nextPage() {
		if (this.currentPage() < this.totalPages()) {
			this.goToPageSelect(this.currentPage() + 1, false);
		}
	}

	zoomIn() {
		this.zoomLevel.update((v) => Math.min(v + 0.25, 3));
	}

	zoomOut() {
		this.zoomLevel.update((v) => Math.max(v - 0.25, 0.5));
	}

	toggleFullscreen() {
		this.showDettaglioSheet.set(false);
		this.isFullscreen.update((v) => !v);
	}

	openRifiutaModal() {
		const modalRef = this.modalService.open(ModaleRifiutaComponent, { centered: true, windowClass: 'modal-app-custom' });
		modalRef.result.then(
			(motivo) => {
				this.rifiuta(motivo);
			},
			() => {},
		);
	}

	rifiuta(motivo: string) {
		const idTrans = this.idTransaction();
		const pd = this.praticaDetail();
		const token = pd?.operationToken;

		if (!idTrans || !token) return;

		this.loadingAction.set(true);
		this.firmaService.refuseTransaction(idTrans, motivo, token).subscribe({
			next: () => {
				this.loadingAction.set(false);
				if (this.isPublic()) {
					this.signatureCompleted.set(true);
				} else {
					this.showSuccess('Pratica rifiutata con successo', 'Hai rifiutato di firmare la pratica.');
					setTimeout(() => {
						this.goBack();
					}, 2500);
				}
			},
			error: (err: any) => {
				this.loadingAction.set(false);
				this.showError('Rifiuto non riuscito', err?.error?.message || 'Si è verificato un errore durante il rifiuto del documento.');
			},
		});
	}

	toggleDettaglio() {
		if (this.showDettaglioSheet()) {
			this.closeDettaglio();
		} else {
			this.showDettaglioSheet.set(true);
		}
	}

	closeDettaglio() {
		this.isSheetClosing.set(true);
		setTimeout(() => {
			this.showDettaglioSheet.set(false);
			this.isSheetClosing.set(false);
		}, 300); // Wait for slideDown animation
	}

	goToPageSelect(page: number, closeSheet = true) {
		this.currentPage.set(page);
		const element = document.getElementById(`page-${page}`);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
		if (closeSheet) {
			this.closeDettaglio();
		}
	}

	signField(field: any) {
		if (field.firmato || this.modalOpen) return;
		this.modalOpen = true;

		const pd = this.praticaDetail();
		const idTrans = this.idTransaction();
		const token = pd?.operationToken;
		const opId = this.operationID();

		if (!idTrans || !token || !opId || !pd) {
			this.modalOpen = false;
			return;
		}

		if (pd.tipoFirma === 'R') {
			this.modalOpen = false; // goToFirma will handle its own modal logic or call
			this.goToFirma(field.signatureFieldID);
		} else {
			// Default to OES flow for 'O' or null/unknown
			const modalRef = this.modalService.open(ModaleConfermaAzioneComponent, { centered: true, windowClass: 'modal-app-custom' });
			modalRef.componentInstance.title = 'Conferma firma';
			modalRef.componentInstance.message = 'Sei sicuro di voler firmare questo campo?';
			modalRef.componentInstance.iconClass = 'bi-pen';
			modalRef.componentInstance.iconStyle = { 'background-color': 'rgba(1, 182, 255, 0.1)', color: '#01B6FF' };

			modalRef.result.then(
				(res) => {
					this.modalOpen = false;
					if (res) this.confermaFirmaO(field);
				},
				() => {
					this.modalOpen = false;
				},
			);
		}
	}

	isLastSignature(fieldID: number): boolean {
		const pd = this.praticaDetail();
		if (!pd || !pd.signatureFields) return false;
		const unsigned = pd.signatureFields.filter((f) => !f.firmato);
		return unsigned.length === 1 && unsigned[0].signatureFieldID === fieldID;
	}

	confermaFirmaO(field: any) {
		const pd = this.praticaDetail();
		const idTrans = this.idTransaction();
		const token = pd?.operationToken;
		const opId = this.operationID();

		if (!field || !idTrans || !token || !opId) return;

		this.loadingAction.set(true);
		const body = {
			signatureFieldID: field.signatureFieldID,
			documentID: field.documentID,
			operationID: opId,
			gpsInfo: this.gpsInfo(),
			multi: false,
		};

		const isLast = this.isLastSignature(field.signatureFieldID);
		this.firmaService.signDocument(idTrans, body, token).subscribe({
			next: (res: any) => {
				this.loadingAction.set(false);
				if (res?.status === 100) {
					this.handleSignatureCompleted();
				} else if (isLast) {
					this.processConferma();
				} else {
					this.loadDetail(idTrans, true);
					this.showSuccess('Operazione completata', 'La firma del documento è avvenuta correttamente.');
					// Scroll back to the field after a short delay to allow re-render
					setTimeout(() => {
						const el = document.getElementById(`field-${field.signatureFieldID}`);
						if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}, 800);
				}
			},
			error: (err: any) => {
				this.loadingAction.set(false);
				this.showError('Errore firma', err?.error?.message || 'Si è verificato un errore durante la firma del campo.');
			},
		});
	}

	goToFirma(signatureFieldID?: number) {
		if (this.modalOpen) return;
		this.modalOpen = true;

		const pd = this.praticaDetail();
		const idTrans = this.idTransaction();
		const token = pd?.operationToken;
		const opId = this.operationID();

		if (!idTrans || !token || !opId || !pd) {
			this.modalOpen = false;
			return;
		}

		// If no specific field is provided, it's the action bar button
		if (!signatureFieldID) {
			const field = this.firstUnsignedField();
			const flat = this.flattenedPages();
			if (field && flat.length) {
				const pageInfo = flat.find((p) => p.documentID === field.documentID && p.localPage === field.page);
				if (pageInfo) {
					if (pageInfo.globalPage !== this.currentPage()) {
						this.goToPageSelect(pageInfo.globalPage);
						// After page change, scroll to field
						setTimeout(() => {
							const el = document.getElementById(`field-${field.signatureFieldID}`);
							if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
						}, 300);
						this.modalOpen = false;
						return;
					} else {
						// Already on correct page, scroll to field
						const el = document.getElementById(`field-${field.signatureFieldID}`);
						if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}
				}
			}

			// If already there or no fields, and it's Remota, go to OTP
			if (pd.tipoFirma === 'R') {
				const modalRef = this.modalService.open(ModaleOtpComponent, {
					centered: true,
					backdrop: 'static',
					keyboard: false,
					windowClass: 'modal-otp-custom modal-app-custom',
				});
				modalRef.componentInstance.mode = 'remote';
				modalRef.result.then(
					(otp) => {
						this.modalOpen = false;
						if (otp) this.confermaFirmaR(null, otp);
					},
					() => {
						this.modalOpen = false;
					},
				);
			} else {
				this.modalOpen = false;
			}
			return;
		}

		// Specific field navigation (OTP for Remota)
		const modalRef = this.modalService.open(ModaleOtpComponent, {
			centered: true,
			backdrop: 'static',
			keyboard: false,
			windowClass: 'modal-otp-custom modal-app-custom',
		});
		modalRef.componentInstance.mode = 'remote';
		modalRef.result.then(
			(otp) => {
				this.modalOpen = false;
				if (otp) {
					const field = pd.signatureFields.find((f) => f.signatureFieldID === signatureFieldID);
					this.confermaFirmaR(field, otp);
				}
			},
			() => {
				this.modalOpen = false;
			},
		);
	}

	confermaFirmaR(field: any | null, otp: string) {
		const pd = this.praticaDetail();
		const idTrans = this.idTransaction();
		const token = pd?.operationToken;
		const opId = this.operationID();

		if (!idTrans || !token || !opId || !pd) return;

		this.loadingAction.set(true);

		// If field is null, we sign the first unsigned field or all?
		// For mobile, it seems goToFirma is called either for a specific field or generally.
		const fieldToSign = field || pd.signatureFields.find((f) => !f.firmato);
		if (!fieldToSign) {
			this.loadingAction.set(false);
			return;
		}

		const body = {
			signatureFieldID: fieldToSign.signatureFieldID,
			documentID: fieldToSign.documentID,
			operationID: opId,
			gpsInfo: this.gpsInfo(),
			otp: otp,
			multi: this.multiValue(),
		};

		const isLast = this.isLastSignature(fieldToSign.signatureFieldID);
		this.firmaService.signDocument(idTrans, body, token).subscribe({
			next: (res: any) => {
				this.loadingAction.set(false);
				if (res?.status === 100) {
					this.handleSignatureCompleted();
				} else if (isLast) {
					this.processConferma();
				} else {
					this.loadDetail(idTrans, true);
					this.showSuccess('Operazione completata', 'La firma del documento è avvenuta correttamente.');
					// Scroll back to the field after a short delay to allow re-render
					setTimeout(() => {
						const el = document.getElementById(`field-${fieldToSign.signatureFieldID}`);
						if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}, 800);
				}
			},
			error: (err: any) => {
				this.loadingAction.set(false);
				this.showError('Errore firma', err?.error?.message || 'Si è verificato un errore durante la firma del campo.');
			},
		});
	}

	conferma() {
		const modalRef = this.modalService.open(ModaleConfermaAzioneComponent, { centered: true, windowClass: 'modal-app-custom' });

		modalRef.componentInstance.title = 'Conferma firma';
		modalRef.componentInstance.message = 'Sei sicuro di voler firmare il documento?';

		modalRef.result.then(
			(res) => {
				if (res) {
					this.processConferma();
				}
			},
			() => {},
		);
	}

	processConferma() {
		const pd = this.praticaDetail();
		const idTrans = this.idTransaction();
		const token = pd?.operationToken;

		if (!idTrans || !token) return;

		this.loadingAction.set(true);
		this.firmaService.closeTransaction(idTrans, token).subscribe({
			next: () => {
				this.loadingAction.set(false);
				this.handleSignatureCompleted();
			},
			error: (err: any) => {
				this.loadingAction.set(false);
				const serverMsg = err?.error?.message || 'Si è verificato un errore durante la chiusura della pratica.';
				this.showError('Chiusura fallita', serverMsg);
			},
		});
	}

	handleSignatureCompleted() {
		if (this.isPublic()) {
			this.signatureCompleted.set(true);
		} else {
			this.showSuccess('Operazione completata', 'La firma del documento è avvenuta correttamente.');
			setTimeout(() => {
				this.goBack();
			}, 2500);
		}
	}

	goBack() {
		if (this.isPublic()) return;
		this.router.navigate(['/app/dashboard']);
	}
}
